import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext.js';
import { Link } from 'react-router-dom';
import { getUserData, isUserEnrolledInCourse } from '../services/user_info.js';
import {EnrollButton, AccountSpecificMessage} from '../components/EnrollButton.js';
import { BookOpen, MessageSquare, MessageCircle, Clock, Users, ClipboardCheck, Award, Images, UserCircle, StickyNote, Puzzle, Paperclip, Download, Newspaper, LayoutGrid } from "lucide-react";
import { GraduationCap, PlayCircle, User, ArrowLeft, ArrowRight, Lock, Unlock } from 'lucide-react';
import { CheckCircle, Circle } from 'lucide-react';
import "./Dashboard.css";
import "./course_list.css";
import Comments from './comments/comments.js';
import LoadingComponent from './loading_component/loading_component.js';

const extractURLparams = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const utmTags = {};
  utmTags.utm_source = urlParams.get('utm_source') || '';
  utmTags.utm_medium = urlParams.get('utm_medium') || '';
  utmTags.utm_campaign = urlParams.get('utm_campaign') || '';
  utmTags.utm_term = urlParams.get('utm_term') || '';
  utmTags.utm_content = urlParams.get('utm_content') || '';
  utmTags.v = urlParams.get('v') || '';
  return utmTags;
};

const URLparams = extractURLparams();
const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;
const base_URL = process.env.REACT_APP_BASE_URL;

function formatDuration(totalSeconds) {
  if (!totalSeconds && totalSeconds !== 0) return null;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts = [];
  if (hours > 0) parts.push(hours.toString());
  if (hours > 0 || minutes > 0) parts.push(minutes.toString().padStart(parts.length > 0 ? 2 : 1, "0"));
  parts.push(seconds.toString().padStart(parts.length > 0 ? 2 : 1, "0"));

  return parts.join(":");
};

function formatDurationShort(totalSeconds) {
  if (totalSeconds == null || isNaN(totalSeconds)) return null;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const parts = [];

  if (hours > 0) parts.push(`${hours}h `);
  if (minutes > 0) parts.push(`${minutes}m `);
  
  // Always show seconds unless ALL are zero (rare, but safe)
  if (seconds > 0 || parts.length === 0) {
    parts.push(`${seconds}s`);
  }

  return parts.join("");
};

const sumSectionLength = (section) => {
  return section.lessons.reduce((acc, lesson) => {
    const len = Number(lesson.length);
    return acc + (isNaN(len) ? 0 : len);
  }, 0);
};

function Video({ videoId, isMobileDevice, videoThumbnailURL }) {
  const video_src = `https://iframe.mediadelivery.net/embed/236258/${videoId}?autoplay=false&loop=false&muted=false&preload=true&responsive=true`;
  return (
    <div className="watch-container">
      <iframe
        src={video_src}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen={true}
        title="Embedded Video"
        poster={videoThumbnailURL}
      ></iframe>
    </div>
  );
}

// Custom hook to detect mobile device
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

async function getCompletedLessons(token) {
  try {
    const response = await fetch(`${baseAPI_URL}/users/get-completed-lessons`, {
      headers: {
          Authorization: `Bearer ${token}`
        }
    });
    if (!response.ok) throw new Error("Failed to fetch completed lessons");

    const data = await response.json();
    return data.completed_lessons || {};
  } catch (error) {
    console.error("Error getting completed lessons:", error);
    return {};
  }
}

async function toggleLessonCompletion(token, courseId, sectionId, lessonId) {
  try {
    const response = await fetch(`${baseAPI_URL}/users/update-completed-lessons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        courseId,
        sectionId,
        lessonId
      }),
    });

    if (!response.ok) throw new Error("Failed to update lesson completion");

    const data = await response.json();
    return data.completed_lessons; // Updated JSON from DB

  } catch (error) {
    console.error("Toggle completion error:", error);
    return null;
  }
}

const Lesson = ({ courseId, sectionId, lessonId, onLessonChange, isCompleted, setIsCompleted }) => {
  const [viewState, setViewState] = useState('loading');

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const { user, getIdToken } = useAuth();
  const [lessonInfo, setLessonInfo] = useState({});
  const [previousLesson, setPreviousLesson] = useState({});
  const [nextLesson, setNextLesson] = useState({});
  const [videoId, setVideoId] = useState('');
  const [videoInfo, setVideoInfo] = useState({});
  const [videoThumbnailURL, setVideoThumbnailURL] = useState('');

  const handleDownloadAttachment = async (fileKey, originalFilename) => {
    try {
      const token = await getIdToken();
      
      const encodedKey = encodeURIComponent(fileKey);
      
      const response = await fetch(
        `${baseAPI_URL}/attachments/presigned-url?key=${encodedKey}`, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get download URL');
      }

      const { url } = await response.json();

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalFilename || 'attachment');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error downloading attachment:', error);
      alert('Erro ao baixar o anexo. Tente novamente mais tarde.');
    }
  };

  // Fetch lesson data
  const getLesson = async () => {
    try {
      setViewState('loading');

      const token = await getIdToken();

      const response = await fetch(
        `${baseAPI_URL}/courses/${courseId}/sections/${sectionId}/lessons/${lessonId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 403) {
        setViewState('upgrade_needed');
        return;
      }

      if (response.status === 404) {
        setViewState('not_found');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch lesson data');
      }

      const data = await response.json();

      setLessonInfo(data.lesson);
      setPreviousLesson(data.prevLesson);
      setNextLesson(data.nextLesson);
      setVideoId(data.lesson.video_id);

      setViewState('loaded');
    } catch (error) {
      console.error('Lesson fetch error:', error);
      setViewState('not_found');
    }
  };

  const goToPreviousLesson = () => {
    if (previousLesson) {
        navigate(`/courses/${courseId}/sections/${previousLesson.sectionId}/lessons/${previousLesson.lessonId}`);
        if (onLessonChange) {
          onLessonChange(previousLesson.sectionId, previousLesson.lessonId);
        }
    }
  }

  const goToNextLesson = () => {
    if (nextLesson) {
        navigate(`/courses/${courseId}/sections/${nextLesson.sectionId}/lessons/${nextLesson.lessonId}`);
        if (onLessonChange) {
          onLessonChange(nextLesson.sectionId, nextLesson.lessonId);
        }
    }
  }

  const handleToggleCompleted = async () => {
    const token = await getIdToken();
    const updated = await toggleLessonCompletion(
      token,
      courseId,
      sectionId,
      lessonId
    );

    if (updated) {
      const isCompleted =
        updated?.[courseId]?.[sectionId]?.[lessonId] === true;

      setIsCompleted(isCompleted);
      onLessonChange(sectionId, lessonId);
    }
  };

  // Fetch video info when videoId is available
  useEffect(() => {
    const fetchVideoInfo = async () => {
      try {
        const token = await getIdToken();     
        const response = await fetch(`${baseAPI_URL}/videos/videoinfo/${videoId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!response.ok) throw new Error('Failed to fetch video info');

        const data = await response.json();
        setVideoInfo(data);
        setVideoThumbnailURL(data.thumbnail_url);
      } catch (error) {
        console.error('Video info fetch error:', error);
      }
    };

    if (videoId && viewState === 'loaded') {
      fetchVideoInfo();
    }
  }, [videoId, viewState]);

  // Set page title from lesson or video
  useEffect(() => {
    if (lessonInfo.title) {
      document.title = lessonInfo.title;
    } else if (videoInfo.title) {
      document.title = videoInfo.title;
    }
  }, [lessonInfo.title, videoInfo.title]);

  // Fetch lesson data when courseId, sectionId, or lessonId changes
  useEffect(() => {
    getLesson();
    const loadCompletionState = async () => {
      const token = await getIdToken();
      const completedData = await getCompletedLessons(token);

      // Safely read nested path
      const isCompleted =
        completedData?.[courseId]?.[sectionId]?.[lessonId] === true;

      setIsCompleted(isCompleted);
    };

    if (user) {
      loadCompletionState();
    }
  }, [courseId, sectionId, lessonId]);

  if (viewState === 'loading') {
    return (
      <section className="lesson">
        <div className="lesson-container">
          <div className="lesson-state-card">
            <h2>Carregando aula...</h2>
            <p>Aguarde enquanto carregamos o conteúdo.</p>
            <img
              src="/loading_lesson.png"
              alt="Carregando conteúdo..."
              className="lesson-state-image"
            />
          </div>
        </div>
      </section>
    );
  }

  if (viewState === 'not_found') {
    return (
      <section className="lesson">
        <div className="lesson-container">
          <div className="lesson-state-card">
            <h2>Aula não encontrada</h2>
            <p>
              A aula solicitada não existe ou não está mais disponível.
            </p>
            <img
              src="/not_found_lesson.png"
              alt="Aula não encontrada"
              className="lesson-state-image"
            />
          </div>
        </div>
      </section>
    );
  }

  if (viewState === 'upgrade_needed') {
    return (
      <section className="lesson">
        <div className="lesson-container">
          <div className="lesson-upgrade-card">
            <h2>Conteúdo exclusivo para assinantes</h2>
            <p>
              Esta aula faz parte do plano premium. Faça upgrade da sua conta
              ou renove a sua assinatura para acessar este conteúdo.
            </p>
            <img
              src="/upgrade_needed_lesson.png"
              alt="Upgrade necessário"
              className="lesson-state-image"
            />
            <Link to="/premium" className="upgrade-button">
              Fazer upgrade
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="lesson">
      <div className="lesson-container">
        <div className="lesson-main-card">
          <header className="lesson-header">
            <Link to={`/coursepage/${courseId}`} className="lesson-course-link">
              <BookOpen size={15} />
              Página do curso
            </Link>
            <h1 className="lesson-page-title">{lessonInfo.title || "Aula"}</h1>
          </header>

          {videoId && (
            <div className="lesson-video">
              <div className="video-wrapper">
                <Video
                  videoId={videoId}
                  isMobileDevice={isMobile}
                  videoThumbnailURL={videoThumbnailURL}
                />
              </div>
            </div>
          )}

          {lessonInfo.description && (
            <div
              className="lesson-description"
              dangerouslySetInnerHTML={{
                __html: lessonInfo.description || "",
              }}
            />
          )}

          <div className="complete-button-wrapper">
            <div className="carousel-arrows">
              <button
                type="button"
                onClick={goToPreviousLesson}
                className={previousLesson ? "back-next-button" : "back-next-button-disabled"}
                disabled={!previousLesson}
                aria-label="Aula anterior"
              >
                <ArrowLeft size={20} />
              </button>
              <button
                type="button"
                onClick={goToNextLesson}
                className={nextLesson ? "back-next-button" : "back-next-button-disabled"}
                disabled={!nextLesson}
                aria-label="Próxima aula"
              >
                <ArrowRight size={20} />
              </button>
            </div>
            <button
              type="button"
              onClick={handleToggleCompleted}
              className={`complete-button ${isCompleted ? "completed" : ""}`}
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="icon" />
                  Marcado como concluído
                </>
              ) : (
                <>
                  <Circle className="icon" />
                  Marcar como concluído
                </>
              )}
            </button>
          </div>
        </div>

        <div className="lesson-attachments-section">
          <h3>Materiais de Apoio</h3>
          {lessonInfo.attachments && lessonInfo.attachments.length > 0 ? (
            <ul className="attachments-list">
              {lessonInfo.attachments.map((file) => (
                <li key={file.id} className="attachment-item">
                  <div className="attachment-info">
                    <Paperclip className="attachment-icon" size={18} />
                    <span className="attachment-title">
                      {file.title || "Arquivo Anexo"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownloadAttachment(file.link, file.title)}
                    className="attachment-download-btn"
                    title="Baixar arquivo"
                  >
                    <Download size={18} />
                    <span>Baixar</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-attachments-msg">
              Não há arquivos ou materiais anexados a esta aula.
            </p>
          )}
        </div>

        <Comments
          courseId={courseId}
          sectionId={sectionId}
          lessonId={lessonId}
        />
      </div>
    </section>
  );
};

const DASHBOARD_LINKS = [
  {
    to: "/allcourses",
    label: "Cursos",
    description: "Playlists e aulas disponíveis",
    icon: BookOpen,
  },
  {
    to: "/feed",
    label: "Feed",
    description: "Publicações da comunidade",
    icon: Newspaper,
  },
  {
    to: "/network",
    label: "Rede",
    description: "Conheça outros usuários",
    icon: Users,
  },
  {
    to: "/news",
    label: "News",
    description: "Atualizações do site",
    icon: MessageSquare,
  },
  {
    to: "/chats",
    label: "Chats",
    description: "Mensagens diretas",
    icon: MessageCircle,
  },
  {
    to: "/account",
    label: "Sua conta",
    description: "Perfil e configurações",
    icon: UserCircle,
  },
];

const Dashboard2 = () => {
  const { user, getIdToken } = useAuth();
  const [accountType, setAccountType] = useState('free');

  const fetchAccountType = async () => {
    if (!user) return;
    try {
      const token = await getIdToken();   
      const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
        headers: {
            Authorization: `Bearer ${token}`
          }
      });
      const data = await res.json();
      setAccountType(data.account_type);
    } catch (err) {
      console.error("Failed to fetch user data", err);
    }
  };
  
  useEffect(() => {
    fetchAccountType();
  }, []);

  const displayName = user
    ? (user.attributes?.name || user.getUsername())
    : "Carregando nome...";

  return (
    <section className="dashboard-page">
      <div className="dashboard-inner">
        <header className="dashboard-header">
          <div className="dashboard-header-text">
            <h1 className="section-title dashboard-title">
              <LayoutGrid size={28} className="dashboard-title-icon" />
              Dashboard
            </h1>
            <p className="dashboard-greeting">
              Bem-vindo(a), <strong>{displayName}</strong>
            </p>
          </div>
          <AccountSpecificMessage account_type={accountType} />
        </header>

        <nav className="dashboard-nav" aria-label="Atalhos da área logada">
          {DASHBOARD_LINKS.map(({ to, label, description, icon: Icon }) => (
            <Link key={to} to={to} className="dashboard-card">
              <span className="dashboard-card-icon-wrap">
                <Icon className="dashboard-icon" size={22} />
              </span>
              <span className="dashboard-card-text">
                <span className="dashboard-card-label">{label}</span>
                <span className="dashboard-card-desc">{description}</span>
              </span>
            </Link>
          ))}
        </nav>

        <div className="dashboard-panel">
          <h2 className="dashboard-panel-title">Novidade do site - 27/07/26</h2>
          <p className="dashboard-panel-text">
          A área social do Domínio Elétrico está no ar: acompanhe o feed de publicações, veja a rede de pessoas, converse por chat (direto ou em grupo) e personalize a sua página de perfil.
          </p>
          <Link to="/feed" className="dashboard-panel-link">
            Abrir o feed
          </Link>
        </div>

        <div className="dashboard-panel">
          <h2 className="dashboard-panel-title">Seções liberadas</h2>
          <ul className="dashboard-sections-list">
            <li>
              <Link to="/allcourses">Cursos</Link>
              <span> — Veja todos os cursos/playlists disponíveis no site.</span>
            </li>
            <li>
              <Link to="/feed">Feed</Link>
              <span> — Veja e publique posts da comunidade.</span>
            </li>
            <li>
              <Link to="/network">Rede</Link>
              <span> — Conheça outros membros do Domínio Elétrico.</span>
            </li>
            <li>
              <Link to="/news">News</Link>
              <span> — Acompanhe as atualizações do site.</span>
            </li>
            <li>
              <Link to="/chats">Chats</Link>
              <span> — Converse com outros usuários por mensagens diretas.</span>
            </li>
            <li>
              <Link to="/account">Sua conta</Link>
              <span> — Informações e configurações da sua conta.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

const AllCoursesListForLoggedUsersInHome = () => {
  const { login, logout, user, getIdToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [accountType, setAccountType] = useState('free');
  const [error, setError] = useState(null);

  const fetchCourses = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/courses/internal`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch courses');
      const data = await response.json();
      setAccountType(data.account_type);
      setCourses(data.courses);
      setEnrolledCourses(data.courses
      .filter(course => course.userStatus === "enrolled")
      .sort((a, b) => b.recommendedRating - a.recommendedRating));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCourses(false);
    }
  };
  
  useEffect(() => {
    fetchCourses();
  }, []);

  if (loadingCourses) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (courses.length === 0) return <div className="empty-message">Nenhum curso encontrado.</div>;

  return (
    <section className="allcourses-section">
      <div className="allcourses-container">

        <div className="allcourses-header">
          <h2 className="allcourses-title"><strong>{user ? (user.attributes?.name || user.getUsername()) : "Carregando nome..."}</strong>, veja os cursos recomendados para você:</h2>
        </div>

        {/* GRID OF CARDS - Recommended */}
        <div className="allcourses-grid">
          {courses
            .filter(course => course.recommendedRating > 0)
            .sort((a, b) => b.recommendedRating - a.recommendedRating)
            .map(course => (
            <div key={course.code} className="allcourses-card">
              <img
                className="allcourses-thumbnail"
                src={course.thumbnail_url}
                alt={`Thumbnail do curso ${course.title}`}
              />

              <h2 className="allcourses-card-title">{course.title}</h2>

              {/* <p className="allcourses-description">{course.description}</p> */}

              <span className={`badge ${course.availability === "free" ? "free" : "premium"}`}>
                {course.availability === "free" ? "Gratuito" : "Premium"}
              </span>

              <div className="allcourses-bottom">
                <span className="enrollment-badge">
                  {loadingCourses ? (
                    <span className="enrollment-status">Verificando...</span>
                  ) : course.userStatus === "enrolled" ? (
                    <span className="badge enrolled">Iniciado</span>
                  ) : (
                    <span className="badge not-enrolled">Não iniciado</span>
                  )}
                </span>

                <a
                  className="allcourses-link"
                  href={`${base_URL}/coursepage/${course.code}`}
                >
                  {loadingCourses ? (
                    "Verificando..."
                  ) : course.userStatus === "enrolled" ? (
                    "Continuar"
                  ) : (
                    "Conhecer"
                  )}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AllCoursesList2 = () => {
  const { login, logout, user, getIdToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [accountType, setAccountType] = useState('free');
  const [error, setError] = useState(null);

  const fetchCourses = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${baseAPI_URL}/courses/internal`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch courses');
      const data = await response.json();
      setAccountType(data.account_type);
      setCourses(data.courses);
      setEnrolledCourses(data.courses
      .filter(course => course.userStatus === "enrolled")
      .sort((a, b) => b.recommendedRating - a.recommendedRating));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCourses(false);
    }
  };
  
  useEffect(() => {
    fetchCourses();
  }, []);

  if (loadingCourses) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (courses.length === 0) return <div className="empty-message">Nenhum curso encontrado.</div>;

  return (
    <section className="allcourses-section">
      <div className="allcourses-container">
        <div className="allcourses-header">
          <h1 className="allcourses-title">Todos os cursos</h1>
          <AccountSpecificMessage account_type={accountType} />
        </div>

        <p className="allcourses-welcome">
          <strong>{user ? (user.attributes?.name || user.getUsername()) : "Carregando nome..."}</strong>, veja todos os cursos abaixo:
        </p>

        <div className="allcourses-header">
          <h2 className="allcourses-title">Cursos recomendados para você:</h2>
        </div>

        {/* GRID OF CARDS - Recommended */}
        <div className="allcourses-grid">
          {courses
            .filter(course => course.recommendedRating > 0)
            .sort((a, b) => b.recommendedRating - a.recommendedRating)
            .map(course => (
            <div key={course.code} className="allcourses-card">
              <img
                className="allcourses-thumbnail"
                src={course.thumbnail_url}
                alt={`Thumbnail do curso ${course.title}`}
              />

              <h2 className="allcourses-card-title">{course.title}</h2>

              {/* <p className="allcourses-description">{course.description}</p> */}

              <span className={`badge ${course.availability === "free" ? "free" : "premium"}`}>
                {course.availability === "free" ? "Gratuito" : "Premium"}
              </span>

              <div className="allcourses-bottom">
                <span className="enrollment-badge">
                  {loadingCourses ? (
                    <span className="enrollment-status">Verificando...</span>
                  ) : course.userStatus === "enrolled" ? (
                    <span className="badge enrolled">Iniciado</span>
                  ) : (
                    <span className="badge not-enrolled">Não iniciado</span>
                  )}
                </span>

                <a
                  className="allcourses-link"
                  href={`${base_URL}/coursepage/${course.code}`}
                >
                  {loadingCourses ? (
                    "Verificando..."
                  ) : course.userStatus === "enrolled" ? (
                    "Continuar"
                  ) : (
                    "Conhecer"
                  )}
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="allcourses-header">
          <h2 className="allcourses-title">Iniciados:</h2>
        </div>

        {/* GRID OF CARDS - Iniciados */}
        {enrolledCourses.length === 0 ? (
          <div className="empty-placeholder">
            Você não iniciou nenhum curso ainda. Comece agora.
          </div>
        ) : (
          <div className="allcourses-grid">
            {enrolledCourses.map(course => (
              <div key={course.code} className="allcourses-card">
                <img
                  className="allcourses-thumbnail"
                  src={course.thumbnail_url}
                  alt={`Thumbnail do curso ${course.title}`}
                />

                <h2 className="allcourses-card-title">{course.title}</h2>

                <span className={`badge ${course.availability === "free" ? "free" : "premium"}`}>
                  {course.availability === "free" ? "Gratuito" : "Premium"}
                </span>

                <div className="allcourses-bottom">
                  <span className="enrollment-badge">
                    {loadingCourses ? (
                      <span className="enrollment-status">Verificando...</span>
                    ) : (
                      <span className="badge enrolled">Iniciado</span>
                    )}
                  </span>

                  <a
                    className="allcourses-link"
                    href={`${base_URL}/coursepage/${course.code}`}
                  >
                    {loadingCourses ? "Verificando..." : "Continuar"}
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="allcourses-header">
          <h2 className="allcourses-title">Todos os cursos</h2>
        </div>

        {/* GRID OF CARDS */}
        <div className="allcourses-grid">
          {courses.map(course => (
            <div key={course.code} className="allcourses-card">
              <img
                className="allcourses-thumbnail"
                src={course.thumbnail_url}
                alt={`Thumbnail do curso ${course.title}`}
              />

              <h2 className="allcourses-card-title">{course.title}</h2>

              {/* <p className="allcourses-description">{course.description}</p> */}

              <span className={`badge ${course.availability === "free" ? "free" : "premium"}`}>
                {course.availability === "free" ? "Gratuito" : "Premium"}
              </span>

              <div className="allcourses-bottom">
                <span className="enrollment-badge">
                  {loadingCourses ? (
                    <span className="enrollment-status">Verificando...</span>
                  ) : course.userStatus === "enrolled" ? (
                    <span className="badge enrolled">Iniciado</span>
                  ) : (
                    <span className="badge not-enrolled">Não iniciado</span>
                  )}
                </span>

                <a
                  className="allcourses-link"
                  href={`${base_URL}/coursepage/${course.code}`}
                >
                  {loadingCourses ? (
                    "Verificando..."
                  ) : course.userStatus === "enrolled" ? (
                    "Continuar"
                  ) : (
                    "Conhecer"
                  )}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AllCoursesListForHome = () => {
  const { login, logout, user, getIdToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState([]);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCourses = async () => {
    try {
      const token = await getIdToken();   
      const response = await fetch(`${baseAPI_URL}/courses`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Failed to fetch courses');
      const data = await response.json();
      setCourses(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchEnrolledCourses = async () => {
    if (!user) {
      setEnrollmentLoading(false);
      return;
    }
    try {
      const token = await getIdToken();   
      const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json(); // assume it's a list of course IDs
      setEnrolledCourseIds(data.courses_enrolled);
    } catch (err) {
      console.error("Failed to fetch enrolled courses", err);
    } finally {
      setEnrollmentLoading(false);
    }
  };
  
  useEffect(() => {
    fetchCourses();
    fetchEnrolledCourses();
  }, []);

  if (loadingCourses) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (courses.length === 0) return <div className="empty-message">Nenhum curso encontrado.</div>;

  return (
    <div className="course-container">
      <h1>Todos os cursos disponíveis no site</h1>
      <div className="courses-grid">
        {courses.map(course => (
          <div key={course.code} className="course-card">
            <h2>{course.title}</h2>
            <img className="thumbnail" src={`/${course.thumbnail_url}`} alt="Thumbnail do curso" />
            <p>{course.description}</p>
            <p><strong>Professor:</strong> {course.instructor}</p>
            {course.availability === 'free' ? (
            <span className="free-course">Gratuito</span>
            ):(
            <span className="premium-course">Premium</span>
            )}            
{/*             <div className="flex-row">
                <a href={`${base_URL}/coursepage/${course.code}`}>
                  {enrollmentLoading ? (
                      <span>Verificando...</span>
                    ) : enrolledCourseIds.includes(course.code) ? (
                      <span>Continuar</span>
                    ) : (
                      <span>Conhecer</span>
                    )}
                </a>
            </div> */}
            <div className='create-account'><a href='#form'><h4>Criar minha conta</h4></a></div>
          </div>
        ))}
      </div>
    </div>
  );
};


const CourseList = ({ courseId }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);
  const [accountType, setAccountType] = useState('free');
  const { user, getIdToken } = useAuth(); // removed isEnrolledInCourse here
  const navigate = useNavigate();

  const getUserInfo = async () => {
    if (user) {
      try {
        const token = await getIdToken();   
        const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
          headers: {
              Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        setAccountType(data.account_type);
      } catch (err) {
        console.error('Error retrieving userData:', err);
      } finally {
        //console.error('Final error');
      }
    }
  }

  const fetchCourse = async () => {
    try {
      const token = await getIdToken();   
      const res = await fetch(`${baseAPI_URL}/courses/${courseId}`, {
        headers: {
              Authorization: `Bearer ${token}`
          }
      });
      if (!res.ok) {
        throw new Error('Course not found');
      }
      const data = await res.json();
      setCourse(data);
      const enrollStatus = data.userStatus == "enrolled";
      setIsEnrolled(enrollStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEnrollmentLoading(false);
    }
  };

  const onEnroll = () => {
    setIsEnrolled(true);
    if (course) {
      const redirectLink =
        `/courses/${courseId}` +
        `/sections/${course.sections[0].code}` +
        `/lessons/${course.sections[0].lessons[0].code}`;
      navigate(redirectLink);
    }
  };

  useEffect(() => {
    fetchCourse();
    getUserInfo();
  }, [courseId, user]);

  if (loading) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (!course) return <div className="empty-message">Nenhuma informação de curso encontrada.</div>;

  return (
    <div className="course-container">
      <a href={`${base_URL}/allcourses`}>
        Voltar para a listagem de todos os cursos
      </a>
      <div className="course-header">
        <h1>{course.title}</h1>
        <div className="enrollment-status">
          {!user ? (
            <div className="login-prompt">
              <a href="/logon" className="login-link">Faça login</a>
            </div>
          ) : !enrollmentLoading && (
            isEnrolled ? (
              <span className="enrolled-badge">Iniciado</span>
            ) : (
              <EnrollButton 
                courseId={courseId}
                availability={course.availability}
                account_type={accountType}
                onEnroll={() => onEnroll()}
              />
            )
          )}
        </div>
      </div>
      <p>{course.description}</p>
      <p><strong>Professor:</strong> {course.instructor}</p>
      {course.availability === 'free' ? (
      <span className="free-course">Gratuito</span>
      ):(
      <span className="premium-course">Premium</span>
      )}

      <h2>Conteúdo do curso</h2>
      {course.sections.map(section => (
        <div key={section.code} className="course_section">
          <h3>{section.title}</h3>
          <ul className="lessons">
            {section.lessons.map(lesson => (
              <li key={lesson.code} className="lesson">
                <h4 className="lesson-title">{lesson.title}</h4>
                <p className="lesson-description">{lesson.description}</p>
                {isEnrolled && (
                    <a href={`${base_URL}/courses/${courseId}/sections/${section.code}/lessons/${lesson.code}`}>
                    Assistir à aula
                    </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

const courseSlugMap = {
  "playlist-introducao-circuitos-eletricos": "course-gratis",
  "curso-dominio-eletrico": "course-a75cc2",
  "dominio-eletrico-labs": "course-ce8071",
  "matematica-do-eletron": "course-19fb51",
  "como-estudar-melhor-na-engenharia": "course-f96078",
  "erros-eletricos": "course-95f159",
  "exercicios-resolvidos-2020": "course-cf3471",
  "exercicios-resolvidos-2022": "course-0dcebf",
  "exercicios-resolvidos-2023": "course-5c5292",
  "lives-canal-do-eletron": "course-186d6b",
  "multivibrador-astavel": "course-ab9f17",
  "videos-de-experimentos": "course-56b761",
};

const slugCourseMap = {
  "course-gratis": "playlist-introducao-circuitos-eletricos",
  "course-a75cc2": "curso-dominio-eletrico",
  "course-ce8071": "dominio-eletrico-labs",
  "course-19fb51": "matematica-do-eletron",
  "course-f96078": "como-estudar-melhor-na-engenharia",
  "course-95f159": "erros-eletricos",
  "course-cf3471": "exercicios-resolvidos-2020",
  "course-0dcebf": "exercicios-resolvidos-2022",
  "course-5c5292": "exercicios-resolvidos-2023",
  "course-186d6b": "lives-canal-do-eletron",
  "course-ab9f17": "multivibrador-astavel",
  "course-56b761": "videos-de-experimentos"
};

const CourseListPrivate = ({ courseId }) => {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);
  const [accountType, setAccountType] = useState('free');
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();

  const fetchCourse = async () => {
    try {
      const token = await getIdToken();   
      const res = await fetch(`${baseAPI_URL}/courses/${courseId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Course not found');
      const data = await res.json();
      setCourse(data);
      const enrollStatus = data.userStatus == "enrolled";
      setIsEnrolled(enrollStatus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEnrollmentLoading(false);
    }
  };

  const getUserInfo = async () => {
    if (user) {
      try {
        const token = await getIdToken();   
        const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
          headers: {
              Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        setAccountType(data.account_type);
      } catch (err) {
        console.error('Error retrieving userData:', err);
      } finally {
        //console.error('Final error');
      }
    }
  };

  const onEnroll = () => {
    setIsEnrolled(true);
    if (course) {
      const redirectLink =
        `/courses/${courseId}` +
        `/sections/${course.sections[0].code}` +
        `/lessons/${course.sections[0].lessons[0].code}`;
      navigate(redirectLink);
    }
  };

  useEffect(() => {
    fetchCourse();
    getUserInfo();
  }, [courseId, user]);

  const sectionTotals = useMemo(() => {
    if (!course) return {};
    const totals = {};

    course.sections.forEach(section => {
      totals[section.code] = sumSectionLength(section);
    });

    return totals;
  }, [course]);

  const courseTotal = useMemo(() => {
    if (!course) return 0;
    return course.sections.reduce(
      (acc, section) => acc + sumSectionLength(section),
      0
    );
  }, [course]);

  if (loading) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (!course) return <div className="empty-message">Nenhuma informação de curso encontrada.</div>;

  return (
    <section className="course-public">
      <div className="course-public-container">
        <p className="back-to-courses">
            <a href={`${base_URL}/allcourses`}>
                Voltar para a listagem de todos os cursos
            </a>
        </p>
        <div className="course-hero">
          <img
            className="course-thumbnail"
            src={`/${course.thumbnail_url}`}
            alt={`Thumbnail do curso ${course.title}`}
          />
          <div className="course-hero-text">
            <h1 className="course-title">
              <BookOpen size={24} className="icon-title" /> {course.title}
            </h1>
            <p className="course-description">{course.description}</p>

            <div className="course-meta">
              <div className="course-instructor">
                <User size={16} />
                <span><strong>Professor:</strong> {course.instructor}</span>
              </div>
              <div className="course-instructor">
                <Clock size={16} />
                <span><strong>Duração:</strong> {formatDurationShort(courseTotal)}</span>
              </div>
              <div className="course-type">
                {course.availability === 'free' ? (
                  <span className="badge free"><Unlock size={14} /> Gratuito</span>
                ) : (
                  <span className="badge premium"><Lock size={14} /> Premium</span>
                )}
              </div>
            </div>

            <div className="course-cta">
              {!user ? (
                <div className="cta-buttons">
                  <a href="/logon" className="cta-login">
                    <GraduationCap size={18} /> Fazer login
                  </a>

                  {course.availability === 'paid' ? (
                    <a href="#form" className="cta-subscribe">
                      <Lock size={18} /> Assinar agora
                    </a>
                  ) : (
                    <a href="/signup" className="cta-create-free">
                      <Unlock size={18} /> Criar conta gratuita
                    </a>
                  )}
                </div>
              ) : !enrollmentLoading && (
                isEnrolled ? (
                  <span className="badge enrolled"><PlayCircle size={16} /> Iniciado</span>
                ) : (
                  <EnrollButton 
                    courseId={courseId}
                    availability={course.availability}
                    account_type={accountType}
                    onEnroll={() => onEnroll()}
                  />
                )
              )}
            </div>
          </div>
        </div>

        <div className="course-content">
          <h2><BookOpen size={20} /> Conteúdo do curso</h2>
          {course.sections.map(section => (
            <div key={section.code} className="course-section">
              <h3>{section.title}</h3>
              <div className="outside-length-info">
              {sectionTotals[section.code] > 0 && (
                <span className="section-total-length">
                  Duração: {formatDurationShort(sectionTotals[section.code])}
                </span>
              )}
              </div>
              <ul className="lessons">
                {section.lessons.map(lesson => (
                  <li key={lesson.code} className="lesson-listing">
                    <div className="lesson-info">
                      <PlayCircle size={16} className="lesson-icon" />
                      <div>
                        <h4>{lesson.title}</h4>
                        <div className="length-info">
                          <Clock size={12} className="length-icon" />
                          {lesson.length ? (
                            <span className="course-lesson-length">
                              {formatDurationShort(lesson.length)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {isEnrolled && (
                      <a
                        href={`${base_URL}/courses/${courseId}/sections/${section.code}/lessons/${lesson.code}`}
                        className="lesson-link"
                      >
                        Assistir
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CourseListPublic = ({ courseSlug }) => {
  const courseId = courseSlugMap[courseSlug];
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrollmentLoading, setEnrollmentLoading] = useState(true);
  const [accountType, setAccountType] = useState('free');
  const { user, getIdToken } = useAuth();
  const navigate = useNavigate();

  const fetchCourse = async () => {
    try {
      if (!user && courseId == "course-gratis") {
        setCourse({
          "availability": "free",
          "category": "Circuitos El\u00e9tricos/Eletr\u00f4nicos",
          "courseId": "course-gratis",
          "createdAt": "2025-04-21T15:30:00Z",
          "description": "Esta playlist gratuita \u00e9 uma cole\u00e7\u00e3o de aulas, no formato de um minicurso intensivo cobrindo v\u00e1rios t\u00f3picos da disciplina de circuitos el\u00e9tricos.",
          "instructor": "Nicholas Yukio",
          "recommendedRating": 0,
          "sections": [
              {
                  "lessons": [
                      {
                          "description": "<p>Neste v\u00eddeo curto eu fa\u00e7o uma breve explica\u00e7\u00e3o sobre esta playlist/minicurso.</p>\n<p><b>Observa\u00e7\u00f5es adicionais:</b> aqui nesta playlist, muito conte\u00fado \u00e9 apresentado em poucas aulas, de forma que para cobrir todos os principais t\u00f3picos de circuitos el\u00e9tricos, n\u00e3o h\u00e1 tempo suficiente para entrar em detalhes importantes de cada conceito.</p>\n<p>Portanto, se voc\u00ea for um iniciante no estudo de circuitos, n\u00e3o se preocupe se voc\u00ea n\u00e3o conseguir absorver todos os conceitos, porque aqui o objetivo \u00e9 proporcionar uma revis\u00e3o e vis\u00e3o geral do assunto.</p>\n<p>Para estudar com mais calma o assunto de circuitos e atingir um bom n\u00edvel de dom\u00ednio da mat\u00e9ria, acesse os conte\u00fados principal do site com uma conta premium.</p>",
                          "length": "61",
                          "lessonId": "apr-1",
                          "title": "Apresenta\u00e7\u00e3o da playlist/minicurso",
                          "videoId": "ea691c7e-3878-4e09-bb19-d5326367a05c"
                      }
                  ],
                  "sectionId": "apr",
                  "title": "Apresenta\u00e7\u00e3o"
              },
              {
                  "lessons": [
                      {
                          "description": "<p>Esta aula traz a explica\u00e7\u00e3o sobre o que s\u00e3o circuitos el\u00e9tricos e sobre os principais conceitos b\u00e1sicos para o estudo de circuitos el\u00e9tricos: tens\u00e3o el\u00e9trica, carga el\u00e9trica, corrente el\u00e9trica, pot\u00eancia el\u00e9trica e energia el\u00e9trica.</p>\n<p>Lembro que a aula aborda v\u00e1rios conceitos concentrados porque o objetivo aqui \u00e9 mais proporcionar uma revis\u00e3o dos t\u00f3picos abordados e para que voc\u00ea conhe\u00e7a mais o site Dom\u00ednio El\u00e9trico. Portanto, \u00e9 normal que voc\u00ea n\u00e3o entenda perfeitamente todos os conceitos se estiver vendo pela primeira vez.</p>\n<p>Para estudar com mais calma o assunto de circuitos e atingir um bom n\u00edvel de dom\u00ednio da mat\u00e9ria, acesse os conte\u00fados principal do site com uma conta premium.</p>",
                          "length": "2654",
                          "lessonId": "lesson-1",
                          "title": "O que s\u00e3o circuitos el\u00e9tricos",
                          "videoId": "be61c173-b541-4b9f-894d-0d12e09db57a"
                      },
                      {
                          "description": "<p>Esta aula explica o tipo mais b\u00e1sico de circuitos el\u00e9tricos: os circuitos de corrente cont\u00ednua. Com isso, aqui aprendemos a fazer as an\u00e1lises mais b\u00e1sicas, usando as leis de Kirchhoff e diagramas de circuitos.</p>\n<p>Lembro que a aula aborda v\u00e1rios conceitos concentrados porque o objetivo aqui \u00e9 mais proporcionar uma revis\u00e3o dos t\u00f3picos abordados e para que voc\u00ea conhe\u00e7a mais o site Dom\u00ednio El\u00e9trico. Portanto, \u00e9 normal que voc\u00ea n\u00e3o entenda perfeitamente todos os conceitos se estiver vendo pela primeira vez.</p>\n<p>Para estudar com mais calma o assunto de circuitos e atingir um bom n\u00edvel de dom\u00ednio da mat\u00e9ria, acesse os conte\u00fados principal do site com uma conta premium.</p>",
                          "length": "2693",
                          "lessonId": "lesson-2",
                          "title": "Circuitos el\u00e9tricos em corrente cont\u00ednua",
                          "videoId": "04df4a2a-0502-428a-96c9-b7951cb3a313"
                      },
                      {
                          "description": "<p>Esta aula explica o tipo de circuito formado quando conectamos um resistor a um capacitor (RC), ou um resistor a um indutor (RL): o circuito de primeira ordem. Entre os conceitos abordados, est\u00e3o: equa\u00e7\u00e3o diferencial ordin\u00e1ria de primeira ordem, resistor, indutor e capacitor, carga e descarga de capacitor/indutor.</p>\n<p>Lembro que a aula aborda v\u00e1rios conceitos concentrados porque o objetivo aqui \u00e9 mais proporcionar uma revis\u00e3o dos t\u00f3picos abordados e para que voc\u00ea conhe\u00e7a mais o site Dom\u00ednio El\u00e9trico. Portanto, \u00e9 normal que voc\u00ea n\u00e3o entenda perfeitamente todos os conceitos se estiver vendo pela primeira vez.</p>\n<p>Para estudar com mais calma o assunto de circuitos e atingir um bom n\u00edvel de dom\u00ednio da mat\u00e9ria, acesse os conte\u00fados principal do site com uma conta premium.</p>",
                          "length": "2851",
                          "lessonId": "lesson-3",
                          "title": "Circuitos el\u00e9tricos de primeira ordem",
                          "videoId": "e1b606cb-afb5-4a1e-8f16-abfbb8308e4b"
                      },
                      {
                          "description": "<p>Esta aula explica os circuitos em corrente alternada: que s\u00e3o aqueles em que as tens\u00f5es e correntes variam conforme fun\u00e7\u00f5es seno/cosseno. Para isso, utilizamos os conceitos de fasores e imped\u00e2ncias, baseados em n\u00fameros complexos.</p>\n<p>Lembro que a aula aborda v\u00e1rios conceitos concentrados porque o objetivo aqui \u00e9 mais proporcionar uma revis\u00e3o dos t\u00f3picos abordados e para que voc\u00ea conhe\u00e7a mais o site Dom\u00ednio El\u00e9trico. Portanto, \u00e9 normal que voc\u00ea n\u00e3o entenda perfeitamente todos os conceitos se estiver vendo pela primeira vez.</p>\n<p>Para estudar com mais calma o assunto de circuitos e atingir um bom n\u00edvel de dom\u00ednio da mat\u00e9ria, acesse os conte\u00fados principal do site com uma conta premium.</p>",
                          "length": "2671",
                          "lessonId": "lesson-4",
                          "title": "Circuitos el\u00e9tricos de corrente alternada",
                          "videoId": "0d8be8a4-6190-4d6d-97e3-8b434ac8b97f"
                      }
                  ],
                  "sectionId": "aulas",
                  "title": "Aulas"
              },
              {
                  "lessons": [
                      {
                          "description": "<p>Neste v\u00eddeo eu encerro a playlist/minicurso de circuitos el\u00e9tricos, e aproveito para lembrar que foi bastante conte\u00fado em poucas aulas densas, de forma que \u00e9 absolutamente normal que voc\u00ea n\u00e3o tenha dominado todos os conceitos, especialmente se estiver vendo pela primeira vez.</p>\n<p>Espero que tenha gostado desta playlist de introdu\u00e7\u00e3o, e te convido a conhecer o conte\u00fado principal do site (curso Dom\u00ednio El\u00e9trico) com uma assinatura premium.</p>\n<p>Com uma assinatura premium, voc\u00ea ter\u00e1 acesso completo a todos os cursos do site, com aulas que abordam os conceitos com did\u00e1tica e profundidade. Tudo isso com suporte para tirar d\u00favidas comigo.</p>",
                          "length": "69",
                          "lessonId": "fin-1",
                          "title": "Finaliza\u00e7\u00e3o da playlist/minicurso",
                          "videoId": "03402df1-56d2-4937-920a-9473a43a34de"
                      }
                  ],
                  "sectionId": "fin",
                  "title": "Finaliza\u00e7\u00e3o"
              }
          ],
          "thumbnail_url": "introducao_aos_circuitos_eletricos_playlist_thumbnail.jpg",
          "title": "Playlist de Introdu\u00e7\u00e3o aos Circuitos El\u00e9tricos",
          "userStatus": "not-enrolled"
      });
      } else {
        const token = await getIdToken();
        const res = await fetch(`${baseAPI_URL}/courses/${courseId}`, {
          headers: {
                Authorization: `Bearer ${token}`
            }
        });
        if (!res.ok) throw new Error('Course not found');
        const data = await res.json();
        setCourse(data);
        const enrollStatus = data.userStatus == "enrolled";
        setIsEnrolled(enrollStatus);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setEnrollmentLoading(false);
    }
  };

  const getUserInfo = async () => {
    if (user) {
      try {
        const token = await getIdToken();
        const res = await fetch(`${baseAPI_URL}/users/get-user-data`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();

        setAccountType(data.account_type);
      } catch (err) {
        console.error('Error retrieving userData:', err);
      } finally {
        //console.error('Final error');
      }
    }
  };

  const onEnroll = () => {
    setIsEnrolled(true);
    if (course) {
      const redirectLink =
        `/courses/${courseId}` +
        `/sections/${course.sections[0].code}` +
        `/lessons/${course.sections[0].lessons[0].code}`;
      navigate(redirectLink);
    }
  };

  useEffect(() => {
    fetchCourse();
    getUserInfo();
  }, [courseId, user]);

  const sectionTotals = useMemo(() => {
    if (!course) return {};
    const totals = {};

    course.sections.forEach(section => {
      totals[section.code] = sumSectionLength(section);
    });

    return totals;
  }, [course]);

  const courseTotal = useMemo(() => {
    if (!course) return 0;
    return course.sections.reduce(
      (acc, section) => acc + sumSectionLength(section),
      0
    );
  }, [course]);


  if (loading) return <LoadingComponent />;
  if (error) return <div className="error-message">{error}</div>;
  if (!course) return <div className="empty-message">Nenhuma informação de curso encontrada.</div>;

  return (
    <section className="course-public">
      <div className="course-public-container">
        <div className="course-hero">
          <img
            className="course-thumbnail"
            src={`/${course.thumbnail_url}`}
            alt={`Thumbnail do curso ${course.title}`}
          />
          <div className="course-hero-text">
            <h1 className="course-title">
              <BookOpen className="icon-title" /> {course.title}
            </h1>
            <p className="course-description">{course.description}</p>

            <div className="course-meta">
              <div className="course-instructor">
                <User size={16} />
                <a href="#professor"><span><strong>Professor:</strong> {course.instructor}</span></a>
              </div>
              <div className="course-instructor">
                <Clock size={16} />
                <span><strong>Duração:</strong> {formatDurationShort(courseTotal)}</span>
              </div>
              <div className="course-type">
                {course.availability === 'free' ? (
                  <span className="badge free"><Unlock size={14} /> Gratuito</span>
                ) : (
                  <span className="badge premium"><Lock size={14} /> Premium</span>
                )}
              </div>
            </div>

            {
              courseSlug === "curso-dominio-eletrico" ? (
                <>
                    <div className="course-meta">
                    <div className="course-instructor">
                      <Users size={16} />
                      <span><strong><a href="#publico-alvo">Público-alvo</a></strong></span>
                    </div>
                    <div className="course-instructor">
                      <ClipboardCheck size={16} />
                      <span><strong><a href="#requisitos">Requisitos</a></strong></span>
                    </div>
                    <div className="course-instructor">
                      <Award size={16} />
                      <span><strong><a href="#certificado">Certificado</a></strong></span>
                    </div>
                  </div>
                </>
              ) : null
            }
            
            <div className="course-cta">
              {!user ? (
                <div className="cta-buttons">
                  <a href="/logon" className="cta-login">
                    <GraduationCap size={18} /> Fazer login
                  </a>

                  {course.availability === 'paid' ? (
                    <a href="#form" className="cta-subscribe">
                      <Lock size={18} /> Assinar agora
                    </a>
                  ) : (
                    <a href="#form" className="cta-create-free">
                      <Unlock size={18} /> Criar conta gratuita
                    </a>
                  )}
                </div>
              ) : !enrollmentLoading && (
                isEnrolled ? (
                  <span className="badge enrolled"><PlayCircle size={16} /> Iniciado</span>
                ) : (
                  <EnrollButton 
                    courseId={courseId}
                    availability={course.availability}
                    account_type={accountType}
                    onEnroll={() => onEnroll()}
                  />
                )
              )}
            </div>
          </div>
        </div>

        <div className="course-content">
          <h2><BookOpen size={20} /> Conteúdo do curso</h2>
          {course.sections.map(section => (
            <div key={section.code} className="course-section">
              <h3>{section.title}</h3>
              <div className="outside-length-info">
              {sectionTotals[section.code] > 0 && (
                <span className="section-total-length">
                  Duração: {formatDurationShort(sectionTotals[section.code])}
                </span>
              )}
              </div>
              <ul className="lessons">
                {section.lessons.map(lesson => (
                  <li key={lesson.code} className="lesson-listing">
                    <div className="lesson-info">
                      <PlayCircle size={16} className="lesson-icon" />
                      <div>
                        <h4>{lesson.title}</h4>
                        <div className="length-info">
                          <Clock size={12} className="length-icon" />
                          {lesson.length ? (
                            <span className="course-lesson-length">
                              {formatDurationShort(lesson.length)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {isEnrolled ? (
                      <a
                        href={`${base_URL}/courses/${courseId}/sections/${section.code}/lessons/${lesson.code}`}
                        className="lesson-link"
                      >
                        Assistir
                      </a>
                    ) : (
                      <a
                        href="#form"
                        className="lesson-link"
                      >
                        Assistir
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {
            courseSlug === "curso-dominio-eletrico" ? (
              <>
                  <div className="course-content" id="publico-alvo">
                    <h2><Users size={20} /> Público-alvo</h2>
                    <p>
                      Este curso destina a alunos de cursos de engenharia (principalmente elétrica e eletrônica), alunos de cursos técnicos relacionados, bem como profissionais já formados em curso técnico ou de graduação. A proposta é ensinar a matéria a quem tem dificuldade só com o conteúdo da própria escola ou faculdade, ou rever a matéria de circuitos elétricos para aprimoramento profissional.
                    </p>
                  </div>

                  <div className="course-content" id="requisitos">
                    <h2><ClipboardCheck size={20} /> Requisitos</h2>
                    <p>
                      Matemática básica (até ensino médio). Noções básicas de derivadas e integrais pode ajudar em módulos mais avançados, mas apenas se quiser aprofundar. Não é necessário ter equipamentos elétricos/eletrônicos.
                    </p>
                  </div>

                  <div className="course-content" id="certificado">
                    <h2><Award size={20} /> Certificado</h2>
                    <p>
                      Emitido após conclusão de todas as aulas e aprovação em prova escrita. Prezamos pela seriedade do nosso ensino, e queremos evitar que alguém se inscreva e ganhe um certificado apenas marcando as aulas como concluídas, sem de fato estudar o curso.
                    </p>
                  </div>
              </>
            ) : null
        }

        <div className="course-content" id="professor">
          <h2><GraduationCap size={20} /> Sobre o Prof. Nicholas Yukio</h2>
          <p>
            Engenheiro eletrônico formado no ITA em 2017. Após dois anos de experiência como professor da matéria de circuitos elétricos no curso de graduação em Engenharia Eletrônica no ITA, criei o curso Domínio Elétrico no início de 2020, que atualmente conta com centenas de alunos de diversas faculdades e escolas.
          </p>
          <img
            className="profile-photo-bio"
            src={'/foto_pessoal_pequena-768x765.jpg'}
            alt={`Foto do Prof. Nicholas Yukio`}
          />
        </div>
      </div>
    </section>
  );
};

const ScreenshotsCarouselPublic = () => {
  const scrollRef = useRef(null);

  const [images, setImages] = useState(
    [
      {
        url: "./screenshots/screenshot_01.jpg",
        caption: "Página de aula do curso Domínio Elétrico, mostrando a aula (Análise Nodal com fontes de tensão)"
      },
      {
        url: "./screenshots/screenshot_02.jpg",
        caption: "Aula do curso Domínio Elétrico (Exercício Resolvido #8 - Equivalente Thévenin)"
      },
      {
        url: "./screenshots/screenshot_03.jpg",
        caption: "Aula do curso Domínio Elétrico (Análise Avançada de Circuitos - Exercícios Resolvidos)"
      },
      {
        url: "./screenshots/screenshot_04.jpg",
        caption: "Página que mostra todos os cursos, os que você está fazendo e os que ainda pode começar."
      },
      {
        url: "./screenshots/screenshot_05.jpg",
        caption: "Dashboard da plataforma, que mostra links para as principais partes da área interna e últimas atualizações do site."
      },
      {
        url: "./screenshots/screenshot_06.jpg",
        caption: "Fórum de discussão para assuntos técnicos e profissionais."
      },
      {
        url: "./screenshots/screenshot_07.jpg",
        caption: "Mensagens diretas (chats 1x1) para conversar individualmente."
      },
      {
        url: "./screenshots/screenshot_08.jpg",
        caption: "Aula do Domínio Elétrico Labs, mostrando um circuito RC + Schmitt Trigger."
      },
      {
        url: "./screenshots/screenshot_09.jpg",
        caption: "Aula do Domínio Elétrico Labs, mostrando um oscilador Colpitts."
      },
      {
        url: "./screenshots/screenshot_10.jpg",
        caption: "Aula do Domínio Elétrico Labs, mostrando teste de circuito eletrônico com Osciloscópio."
      }
    ]
  );

  /* useEffect(() => {
    fetch(`${baseAPI_URL}/courses`)
      .then(res => res.json())
      .then(data => setCourses(data));
  }, []); */

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section id="screenshots" className="carousel-section">
      <div className="carousel-header">
        <div className="blur">
          <h2>Screenshots</h2>
        </div>
        <div className="carousel-arrows">
          <button onClick={scrollLeft} className="carousel-arrow-btn">
            <ArrowLeft size={18} />
          </button>
          <button onClick={scrollRight} className="carousel-arrow-btn">
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <div className="carousel-wrapper" ref={scrollRef}>
        {images.map((image) => {
          if (!image) return null;
          return (
            <div className="carousel-card">
              <img
                src={`/${image.url}`}
                alt={image.caption}
                className="carousel-thumbnail-screenshot"
              />
              <div className="carousel-card-body">
                <p className="carousel-description">{image.caption}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
  };

const CoursesCarouselPublic = ({ place }) => {
  const scrollRef = useRef(null);

  const [courses, setCourses] = useState([]);

  useEffect(() => {
    fetch(`${baseAPI_URL}/courses?place=${place}`)
      .then(res => res.json())
      .then(data => setCourses(data));
  }, []);

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="carousel-section">
      <div className="carousel-header">
        <div className="blur">
          <h2>Nossos cursos</h2>
        </div>
        <div className="carousel-arrows">
          <button onClick={scrollLeft} className="carousel-arrow-btn">
            <ArrowLeft size={18} />
          </button>
          <button onClick={scrollRight} className="carousel-arrow-btn">
            <ArrowRight size={18} />
          </button>
        </div>
      </div>

      <div className="carousel-wrapper" ref={scrollRef}>
        {courses.map((course) => {
          if (!course) return null;
          return (
            <div key={course.code} className="carousel-card">
              <img
                src={`/${course.thumbnail_url}`}
                alt={`Thumbnail do curso ${course.title}`}
                className="carousel-thumbnail"
              />
              <div className="carousel-card-body">
                <h3><a href={`${base_URL}/public-course-page/${slugCourseMap[course.code]}`}>{course.title}</a></h3>
                <p className="carousel-description">{course.description}</p>
                <div className="carousel-meta">
                  {course.availability === 'free' ? (
                    <span className="badge free"><Unlock size={14} /> Gratuito</span>
                  ) : (
                    <span className="badge premium"><Lock size={14} /> Premium</span>
                  )}
                </div>
                <a href={`${base_URL}/public-course-page/${slugCourseMap[course.code]}`} className="carousel-link">
                  Conhecer
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
  };

export {AllCoursesList2, AllCoursesListForLoggedUsersInHome, AllCoursesListForHome, CourseList, CourseListPublic, CourseListPrivate, CoursesCarouselPublic, Lesson, Dashboard2, ScreenshotsCarouselPublic};
